import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearQueryCache } from '../lib/queryClient'
import type { User } from './authStore'

interface AdminState {
  isImpersonating: boolean
  originalToken: string | null
  originalUser: User | null
  impersonatedUser: User | null

  startImpersonation: (user: User, token: string) => void
  stopImpersonation: () => void
}

export const useAdminStore = create<AdminState>()(
  persist(
    (set, get) => ({
      isImpersonating: false,
      originalToken: null,
      originalUser: null,
      impersonatedUser: null,

      startImpersonation: (user: User, token: string) => {
        // Save current admin state before switching
        const currentToken = localStorage.getItem('gestorplus_token')
        const authState = JSON.parse(
          localStorage.getItem('gestorplus-auth') || '{}'
        )
        const currentUser = authState?.state?.user || null

        // Set the impersonated token as active
        localStorage.setItem('gestorplus_token', token)

        // Update auth store in localStorage
        const newAuthState = {
          ...authState,
          state: {
            ...authState.state,
            user: user,
            token: token,
            isAuthenticated: true,
          },
        }
        localStorage.setItem('gestorplus-auth', JSON.stringify(newAuthState))

        set({
          isImpersonating: true,
          originalToken: currentToken,
          originalUser: currentUser,
          impersonatedUser: user,
        })

        // Clear query cache so data reloads for the impersonated user
        clearQueryCache()

        // Reload to pick up new auth state
        window.location.href = '/dashboard'
      },

      stopImpersonation: () => {
        const { originalToken, originalUser } = get()

        if (originalToken && originalUser) {
          // Restore admin token
          localStorage.setItem('gestorplus_token', originalToken)

          // Restore auth store
          const authState = JSON.parse(
            localStorage.getItem('gestorplus-auth') || '{}'
          )
          const restoredAuthState = {
            ...authState,
            state: {
              ...authState.state,
              user: originalUser,
              token: originalToken,
              isAuthenticated: true,
            },
          }
          localStorage.setItem(
            'gestorplus-auth',
            JSON.stringify(restoredAuthState)
          )
        }

        set({
          isImpersonating: false,
          originalToken: null,
          originalUser: null,
          impersonatedUser: null,
        })

        clearQueryCache()
        window.location.href = '/dashboard/admin/users'
      },
    }),
    {
      name: 'gestorplus-admin',
      partialize: (state) => ({
        isImpersonating: state.isImpersonating,
        originalToken: state.originalToken,
        originalUser: state.originalUser,
        impersonatedUser: state.impersonatedUser,
      }),
    }
  )
)
